function captureError(code) {
    const error = new Error(code);
    error.code = code;
    return error;
}

function waitForFrame(video, stream, signal) {
    return new Promise((resolve, reject) => {
        let frameId;
        let finished = false;
        const track = stream.getVideoTracks()[0];
        const finish = (error) => {
            if (finished) return;
            finished = true;
            clearTimeout(timeout);
            if (frameId != null) video.cancelVideoFrameCallback(frameId);
            video.removeEventListener('loadeddata', onLoaded);
            video.removeEventListener('error', onError);
            track?.removeEventListener('ended', onError);
            signal?.removeEventListener('abort', onAbort);
            if (error) reject(error);
            else resolve();
        };
        const onFrame = () => finish();
        const onLoaded = () => {
            if (!video.requestVideoFrameCallback) finish();
        };
        const onError = () => finish(captureError('captureFailed'));
        const onAbort = () => finish(captureError('aborted'));
        const timeout = setTimeout(() => finish(captureError('captureFailed')), 10000);
        video.addEventListener('loadeddata', onLoaded);
        video.addEventListener('error', onError);
        track?.addEventListener('ended', onError);
        signal?.addEventListener('abort', onAbort);
        try {
            if (video.requestVideoFrameCallback) frameId = video.requestVideoFrameCallback(onFrame);
            video.srcObject = stream;
            video.play().catch(onError);
        } catch (error) {
            onError();
        }
    });
}

// Call directly from the click handler: the browser requires transient user activation.
// The canvas copies pixels from the display stream; it never renders the page's HTML.
export async function captureScreenshot({ signal } = {}) {
    if (!navigator.mediaDevices?.getDisplayMedia) throw captureError('unsupported');
    if (signal?.aborted) throw captureError('aborted');

    const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: 'browser' },
        audio: false,
        preferCurrentTab: true,
        selfBrowserSurface: 'include',
        systemAudio: 'exclude',
    });
    const stop = () => stream.getTracks().forEach(track => track.stop());
    let video;
    try {
        // A picker cannot be dismissed programmatically. If the page was left while
        // it was open, immediately stop any stream it eventually returns.
        if (signal?.aborted) throw captureError('aborted');
        signal?.addEventListener('abort', stop);
        video = document.createElement('video');
        video.muted = true;
        video.playsInline = true;
        // Keep a rendered video sink while waiting for the first frame. A detached
        // element can stall without receiving any video data in Chrome.
        video.tabIndex = -1;
        video.setAttribute('aria-hidden', 'true');
        video.style.cssText =
            'position: fixed; top: 0; left: 0; width: 1px; height: 1px; opacity: 0; pointer-events: none;';
        document.body.appendChild(video);
        await waitForFrame(video, stream, signal);
        if (signal?.aborted) throw captureError('aborted');
        if (!video.videoWidth || !video.videoHeight) throw captureError('captureFailed');

        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const context = canvas.getContext('2d');
        if (!context) throw captureError('captureFailed');
        context.drawImage(video, 0, 0);
        // Sharing ends as soon as the frame has been copied, before PNG encoding/upload.
        stop();
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
        if (!blob) throw captureError('captureFailed');
        if (signal?.aborted) throw captureError('aborted');
        return new File([blob], `support-screenshot-${Date.now()}.png`, { type: 'image/png' });
    } finally {
        signal?.removeEventListener('abort', stop);
        stop();
        if (video) {
            video.pause();
            video.srcObject = null;
            video.remove();
        }
    }
}

export function getSupportReportContext(appState) {
    // Keep the hash-router path, excluding query parameters that can contain credentials.
    const page = window.location.origin + window.location.pathname + window.location.hash.split('?')[0];
    return [
        `Page: ${page}`,
        `App version: ${appState.currentVersion || ''}`,
        `Browser: ${navigator.userAgent}`,
        `Viewport: ${window.innerWidth} x ${window.innerHeight}`,
        `Report started at: ${new Date().toISOString()}`,
    ].join('\n');
}
